/**
 * SlashCommandSuggestions Component (Issue #302)
 *
 * The mode list, reached by typing '/' instead of clicking the mode chip.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover';
import { useAppSelector } from '@store/index';
import { selectActiveModes, selectCurrentMode } from '@features/chat/chatModeSlice';
import { SLASH_COMMANDS, SlashCommand } from '../../types/chat';
import { visibleSlashCommands } from './chatIntent';
import {
  ChatModeOptionContent,
  modeColorStyle,
  modeOptionClassName,
} from './ChatModeOption';

interface SlashCommandSuggestionsProps {
  inputValue: string;
  anchorRef: React.RefObject<HTMLElement | null>;
  onSelectCommand: (command: SlashCommand) => void;
  onClose: () => void;
  isVisible?: boolean;
}

const SlashCommandSuggestions: React.FC<SlashCommandSuggestionsProps> = ({
  inputValue,
  anchorRef,
  onSelectCommand,
  onClose,
  isVisible = true,
}) => {
  const { t } = useTranslation();
  const modes = useAppSelector(selectActiveModes);
  const currentModeId = useAppSelector(selectCurrentMode);
  const [highlighted, setHighlighted] = useState(0);

  // Joined against the modes so a command shows the same icon, colour and
  // description as the picker does. The two used to carry separate copies.
  const commands = useMemo(
    () => visibleSlashCommands(SLASH_COMMANDS, inputValue, modes),
    [inputValue, modes],
  );

  const isOpen = commands.length > 0 && isVisible;

  useEffect(() => setHighlighted(0), [inputValue]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlighted((prev) => (prev + 1) % commands.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlighted((prev) => (prev - 1 + commands.length) % commands.length);
          break;
        case 'Enter':
        case 'Tab':
          if (commands[highlighted]) {
            event.preventDefault();
            onSelectCommand(commands[highlighted].command);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    },
    [isOpen, commands, highlighted, onSelectCommand, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Popover open={isOpen}>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="revy-ui w-[320px] p-0"
        // The composer keeps focus: this list is driven from the keys typed
        // there, and stealing focus would stop the query from updating.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <p className="border-b px-2.5 py-2 text-xs text-muted-foreground">{t('chat.commands')}</p>

        <div
          role="listbox"
          aria-label={t('chat.commands')}
          className="flex max-h-[300px] flex-col gap-0.5 overflow-y-auto overscroll-contain p-1.5"
        >
          {commands.map(({ command, mode }, index) => (
            <button
              key={command.command}
              type="button"
              role="option"
              aria-selected={index === highlighted}
              onMouseEnter={() => setHighlighted(index)}
              onClick={() => onSelectCommand(command)}
              style={modeColorStyle(mode)}
              className={modeOptionClassName(index === highlighted)}
            >
              <ChatModeOptionContent
                mode={mode}
                selected={mode.id === currentModeId}
                highlighted={index === highlighted}
                showEnterHint
              />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SlashCommandSuggestions;
